import {t} from '@lingui/macro';
import {Graph} from '@visx/network';
import {NodeProvidedProps} from '@visx/network/lib/types';
import React, {FC, useMemo} from 'react';

import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {ChannelModel} from '../../../models/api/channelModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {RuleBundleModel, RuleBundleModelStrict} from '../../../models/api/ruleBundleModel';
import {RuleBundleTypesEnum} from '../../../models/api/ruleCommonTypes';
import {CompoundConditions} from '../../editors/conditions/compoundConditionModel';
import {RuleAction} from '../../rules/ruleActionModel';
import {ImpactedInboxes} from '../../rules/ruleSimulationHelpers';
import {
  INBOX_NODE_HEIGHT,
  InboxGraphAnyInboxNode,
  InboxGraphBundlesNode,
  InboxGraphChannelNode,
  InboxGraphInboxNode,
  InboxGraphInheritableProps,
  InboxGraphLink,
  InboxGraphMainInboxNode,
  InboxGraphNode,
  InboxGraphNodeTypesEnum,
  InboxGraphProps,
  MAIN_INBOX_NODE_HEIGHT,
  NODE_WIDTH,
} from './inboxGraphTypes';
import {LinkComponent} from './linkComponent';
import {BundlesNodeComponent} from './nodes/bundlesNodeComponent';
import {ChannelNodeComponent} from './nodes/channelNodeComponent';
import {AnyInboxNodeComponent, InboxNodeComponent, MainInboxNodeComponent} from './nodes/inboxNodeComponent';

/*
 * Types.
 */

type Direction = 'in' | 'out';
type NodeWithDirection = InboxGraphNode & {direction: Direction};

const NODE_DISTANCE_X = 224;
const BUNDLES_TO_MAIN_INBOX = 177;

/*
 * Helpers.
 */

/**
 * Returns the NodeModel used to display a node representing a channel.
 */
function getChannelNode(
  channel: ChannelModel,
  x: number,
  y: number,
  goToChannel: InboxGraphInheritableProps['goToChannel'],
  getUniqueChannelType: InboxGraphInheritableProps['getUniqueChannelType'],
): InboxGraphChannelNode {
  return {
    id: `channel_${channel.id}`,
    type: InboxGraphNodeTypesEnum.CHANNEL,
    label: channel.displayName,
    onClick: () => goToChannel({channelId: channel.id}),
    uniqueChannelType: getUniqueChannelType(channel),
    x,
    y,
  };
}

/**
 * Returns the NodeModel used to display a node representing the main inbox.
 */
function getMainInboxNode(inbox: InboxModel, x: number, y: number): InboxGraphMainInboxNode {
  return {
    id: `inbox_${inbox.id}`,
    type: InboxGraphNodeTypesEnum.MAIN_INBOX,
    label: inbox.name,
    iconColor: inbox.color,
    x,
    y,
  };
}

/**
 * Returns the NodeModel used to display a node representing an inbox.
 */
function getInboxNode(
  inbox: InboxModel,
  x: number,
  y: number,
  goToInbox: InboxGraphInheritableProps['goToInbox'],
): InboxGraphInboxNode {
  return {
    id: `inbox_${inbox.id}`,
    type: InboxGraphNodeTypesEnum.INBOX,
    label: inbox.name,
    onClick: () => goToInbox(inbox.id),
    iconColor: inbox.color,
    x,
    y,
  };
}

/**
 * Returns the NodeModel used to display a node representing any inbox.
 */
function getAnyInboxNode(x: number, y: number, id: string, label: string): InboxGraphAnyInboxNode {
  return {
    id,
    type: InboxGraphNodeTypesEnum.ANY_INBOX,
    label,
    x,
    y,
  };
}

/**
 * Returns the NodeModel used to display a node representing a bundles list.
 */
function getBundlesNode(
  bundles: ReadonlyArray<RuleBundleModel>,
  x: number,
  y: number,
  id: string,
  goToInboxRules: InboxGraphInheritableProps['goToInboxRules'],
  goToBundle: InboxGraphInheritableProps['goToBundle'],
): InboxGraphBundlesNode {
  return {
    id,
    type: InboxGraphNodeTypesEnum.BUNDLES,
    label: bundles[0].name,
    onClick: (bundle: RuleBundleModel | undefined) => {
      if (bundle === undefined) {
        return goToInboxRules();
      }

      return goToBundle(bundle);
    },
    bundles,
    x,
    y,
  };
}

const listImpactedInboxesByBundle = (
  ruleBundle: RuleBundleModel,
  listImpactedInboxes: (compoundConditions: CompoundConditions) => ImpactedInboxes,
) => {
  switch (ruleBundle.type) {
    case RuleBundleTypesEnum.SINGLE:
      return listImpactedInboxes(ruleBundle.rules[0].compoundConditions);
    case RuleBundleTypesEnum.SLA: {
      const breachRule = ruleBundle.rules.find((r) => r.role === 'breach');
      if (!breachRule) {
        return null;
      }
      return listImpactedInboxes(breachRule.compoundConditions);
    }
    default:
      return null;
  }
};

/*
 * Components.
 */

/**
 * Display a graph representing all the inputs and outputs of an inbox.
 */
export const InboxGraph: FC<InboxGraphProps> = ({
  inbox,
  moveInBundles,
  moveOutBundles,
  listImpactedInboxes,
  getFedInbox,
  goToChannel,
  goToInboxRules,
  goToInbox,
  goToBundle,
  getInboxById,
  channels,
  getUniqueChannelType,
}) => {
  // Expensive calculation, so let's not do it too often.
  const fromInboxes = useMemo(
    () => getInboxesIn(moveInBundles, listImpactedInboxes),
    [listImpactedInboxes, moveInBundles],
  );
  const localize = useLocalize();

  // Model this function will populate and therefor use to generate the graph.
  const nodesWithDirection: NodeWithDirection[] = []; // All the nodes that are in the graph.
  const nodeLinks: InboxGraphLink[] = []; // All the links between nodes.

  // Create a node for the current inbox.
  const mainInboxNode = getMainInboxNode(inbox, 0, 0); // x and y will be set later on depending on the number of input / output.

  // Indexes to track the number of node chains going in/out the main inbox already added to the graph.
  let inIndex = 0;
  let outIndex = 0;

  // All inboxes from which conv are moved to this inbox by bundles.
  const {
    fromSpecificInbox: inboxInToBundlesMap,
    fromAnyInbox,
    fromAnyIndividualInbox,
    fromAnyTeamInbox,
  } = fromInboxes;

  // Compute all inboxes to which conv are moved from this inbox by bundles.
  const inboxOutToBundlesMap = getInboxesOut(moveOutBundles, getFedInbox);

  // Compute the total number of node chains going in/out the inbox.
  const nbChainIn =
    channels.length +
    inboxInToBundlesMap.size +
    (fromAnyInbox.length > 0 ? 1 : 0) +
    (fromAnyIndividualInbox.length > 0 ? 1 : 0) +
    (fromAnyTeamInbox.length > 0 ? 1 : 0);
  const nbChainOut = inboxOutToBundlesMap.size;

  // Compute distances between different layers (inbox layer, bundles layer, main inbox layer, etc.).

  const INBOX_TO_BUNDLES = nbChainIn > 1 || nbChainOut > 1 ? 128 : BUNDLES_TO_MAIN_INBOX;
  const INBOX_TO_MAIN_INBOX = INBOX_TO_BUNDLES + BUNDLES_TO_MAIN_INBOX;

  mainInboxNode.y = nbChainIn === 0 ? 0 : INBOX_TO_MAIN_INBOX; // If no input main inbox is at the top of the graph, at INBOX_TO_MAIN_INBOX otherwise.

  // -- INPUT NODEs --

  // Create a node for each channel feeding the inbox with conv.
  channels.forEach((channel: ChannelModel) => {
    const channelNode = getChannelNode(
      channel,
      inIndex * NODE_DISTANCE_X,
      0,
      goToChannel,
      getUniqueChannelType,
    );
    addNodesChain({
      subNodes: [channelNode],
      direction: 'in',
      mainInboxNode,
      nodesWithDirection,
      nodeLinks,
      updateIndex: (offset) => {
        inIndex += offset;
      },
    });
  });

  // Create a node for each inbox a conversation can come from (with an intermediate bundles node).
  inboxInToBundlesMap.forEach((bundles, inboxInId) => {
    const inboxIn = getInboxById(inboxInId);
    if (!inboxIn) {
      return;
    }
    const inboxInNode = getInboxNode(inboxIn, inIndex * NODE_DISTANCE_X, 0, goToInbox);
    const bundlesNode = getBundlesNode(
      bundles,
      inIndex * NODE_DISTANCE_X,
      INBOX_TO_BUNDLES,
      `from_${inboxInId}`,
      goToInboxRules,
      goToBundle,
    );

    addNodesChain({
      subNodes: [inboxInNode, bundlesNode],
      direction: 'in',
      mainInboxNode,
      nodesWithDirection,
      nodeLinks,
      updateIndex: (offset) => {
        inIndex += offset;
      },
    });
  });

  // Create an "any inbox" nodes if some bundles move conv from any inbox, any team inbox, or any individual inbox, to this inbox (with an intermediate bundles node).
  [
    {
      bundles: fromAnyInbox,
      nodeName: localize(t('inboxgraph-any-inbox')`Any inbox`),
      nodeId: 'any_inbox',
    },
    {
      bundles: fromAnyTeamInbox,
      nodeName: localize(t('inboxgraph-any-teaminbox')`Any team inbox`),
      nodeId: 'any_team_inbox',
    },
    {
      bundles: fromAnyIndividualInbox,
      nodeName: localize(t('inboxgraph-any-individualinbox')`Any individual inbox`),
      nodeId: 'any_individual_inbox',
    },
  ].forEach(({bundles, nodeName, nodeId}) => {
    if (bundles.length > 0) {
      const anyInboxNode = getAnyInboxNode(inIndex * NODE_DISTANCE_X, 0, nodeId, nodeName);
      const bundlesNode = getBundlesNode(
        bundles,
        inIndex * NODE_DISTANCE_X,
        INBOX_TO_BUNDLES,
        'from_any_inbox',
        goToInboxRules,
        goToBundle,
      );
      addNodesChain({
        subNodes: [anyInboxNode, bundlesNode],
        direction: 'in',
        mainInboxNode,
        nodesWithDirection,
        nodeLinks,
        updateIndex: (offset) => {
          inIndex += offset;
        },
      });
    }
  });

  // -- OUTPUT NODEs --

  // Create node for each inbox conversations can go to (with an intermediate bundles node).
  inboxOutToBundlesMap.forEach((bundles, inboxOutId) => {
    const inboxOut = getInboxById(inboxOutId);
    if (!inboxOut) {
      return;
    }

    const bundlesNode = getBundlesNode(
      bundles,
      outIndex * NODE_DISTANCE_X,
      mainInboxNode.y + BUNDLES_TO_MAIN_INBOX,
      `to_${inboxOutId}`,
      goToInboxRules,
      goToBundle,
    );
    const inboxOutNode = getInboxNode(
      inboxOut,
      outIndex * NODE_DISTANCE_X,
      mainInboxNode.y + INBOX_TO_MAIN_INBOX,
      goToInbox,
    );
    addNodesChain({
      subNodes: [bundlesNode, inboxOutNode],
      direction: 'out',
      mainInboxNode,
      nodesWithDirection,
      nodeLinks,
      updateIndex: (offset) => {
        outIndex += offset;
      },
    });
  });

  // -- RENDERING --

  const graphWidth = computeGraphWidth(nbChainIn, nbChainOut);
  const graphHeight = computeGraphHeight(nbChainIn, nbChainOut, INBOX_TO_BUNDLES);

  // Horizontal centering of main inbox.
  mainInboxNode.x = nbChainOut > 1 || nbChainIn > 1 ? (graphWidth - NODE_WIDTH) / 2 : 0;

  // Horizontal centering of the in/out nodes.
  if (nbChainIn !== nbChainOut && nbChainIn > 0 && nbChainOut > 0) {
    const directionNeedingTranslation = nbChainIn > nbChainOut ? 'out' : 'in';
    const horizontalTranslationValue = (Math.abs(nbChainIn - nbChainOut) * NODE_DISTANCE_X) / 2;
    nodesWithDirection.forEach((node) => {
      if (node.direction === directionNeedingTranslation) {
        // eslint-disable-next-line no-param-reassign
        node.x += horizontalTranslationValue;
      }
    });
  }

  // Render graph using VX.
  const graph = {
    nodes: [mainInboxNode, ...nodesWithDirection],
    links: nodeLinks,
  };

  // Adding an offset so that dropped shadows does not get cut.
  const OFFSET = 10;

  return (
    <svg
      viewBox={`${-OFFSET} ${-OFFSET} ${graphWidth + OFFSET * 2} ${graphHeight + OFFSET * 2}`}
      width="100%"
      height="auto"
      className="graph"
    >
      <Graph
        graph={graph}
        linkComponent={LinkComponent}
        nodeComponent={NodeComponent}
        left={NODE_WIDTH / 2}
        top={inIndex > 0 ? INBOX_NODE_HEIGHT / 2 : MAIN_INBOX_NODE_HEIGHT / 2}
      />
    </svg>
  );
};

/**
 * Given a generic NodeModel, return the ReactElement representing this node.
 */
const NodeComponent: FC<NodeProvidedProps<InboxGraphNode>> = ({node}) => {
  const {type} = node;
  switch (type) {
    case InboxGraphNodeTypesEnum.CHANNEL:
      return <ChannelNodeComponent node={node} />;
    case InboxGraphNodeTypesEnum.ANY_INBOX:
      return <AnyInboxNodeComponent node={node} />;
    case InboxGraphNodeTypesEnum.MAIN_INBOX:
      return <MainInboxNodeComponent node={node} />;
    case InboxGraphNodeTypesEnum.INBOX:
      return <InboxNodeComponent node={node} />;
    case InboxGraphNodeTypesEnum.BUNDLES:
      return <BundlesNodeComponent node={node} />;
    default:
      // We should never end up here.
      // @ts-expect-error node should be never
      return <text>{node.label}</text>;
  }
};

function computeGraphWidth(nbChainIn: number, nbChainOut: number) {
  if (nbChainIn <= 1 && nbChainOut <= 1) {
    return NODE_WIDTH;
  }

  return (Math.max(nbChainIn, nbChainOut) - 1) * NODE_DISTANCE_X + NODE_WIDTH;
}

function computeGraphHeight(nbChainIn: number, nbChainOut: number, inboxToBundles: number) {
  if (nbChainIn === 0 && nbChainOut === 0) {
    return MAIN_INBOX_NODE_HEIGHT;
  }

  if (nbChainIn === 0 || nbChainOut === 0) {
    return BUNDLES_TO_MAIN_INBOX + inboxToBundles + (INBOX_NODE_HEIGHT / 2 + MAIN_INBOX_NODE_HEIGHT / 2);
  }

  return 2 * (BUNDLES_TO_MAIN_INBOX + inboxToBundles) + INBOX_NODE_HEIGHT;
}

interface AddNodesChainOptions {
  subNodes: Array<InboxGraphNode>;
  direction: Direction;
  mainInboxNode: InboxGraphMainInboxNode;
  nodesWithDirection: Array<NodeWithDirection>;
  updateIndex: (offset: number) => void;
  nodeLinks: Array<InboxGraphLink>;
}

/**
 * Add a nodes chain to the current model. A nodes chain is a sequence of linked nodes ending up or coming from the main inbox node.
 * Thus update nodes and links:
 * - nodes get enriched with _nodes
 * - links get enriched with new links so that provided _nodes create a chain ending up or coming from the main inbox node (depending on direction).
 */
function addNodesChain({
  subNodes,
  direction,
  mainInboxNode,
  nodesWithDirection,
  nodeLinks,
  updateIndex,
}: AddNodesChainOptions) {
  if (subNodes.length === 0) {
    return;
  }

  const subNodesWithDirection = subNodes.map((node) => ({...node, direction}));

  const nodeChain =
    direction === 'in'
      ? [...subNodesWithDirection, mainInboxNode]
      : [mainInboxNode, ...subNodesWithDirection];
  nodeChain.forEach((node, index) => {
    if (node.id !== mainInboxNode.id) {
      nodesWithDirection.push(node as NodeWithDirection);
    }

    const nextIndex = index + 1;
    if (nextIndex >= nodeChain.length) {
      return;
    }

    const nextNode = nodeChain[nextIndex];
    nodeLinks.push({
      source: node,
      target: nextNode,
    });
  });

  updateIndex(1);
}

/**
 * Compute all the inboxes from which conversations are move into this inbox.
 * Map them to the list of bundles performing this action.
 *
 * @return an object with the following attributes:
 * - fromSpecificInbox: a map of:
 *    - inboxId from which conversation are move into this inbox,
 *    - to a collection of bundles that operate this move action.
 * - fromAnyInbox: list of bundles operating the move action from any individual or team inbox.
 * - fromAnyTeamInbox: list of bundles operating the move action from any team inbox.
 * - fromAnyIndividualInbox: list of bundles operating the move action from any individual inbox.
 */
function getInboxesIn(
  moveInBundles: ReadonlyArray<RuleBundleModelStrict>,
  listImpactedInboxes: InboxGraphInheritableProps['listImpactedInboxes'],
): {
  fromSpecificInbox: Map<number, RuleBundleModel[]>;
  fromAnyInbox: RuleBundleModel[];
  fromAnyTeamInbox: RuleBundleModel[];
  fromAnyIndividualInbox: RuleBundleModel[];
} {
  const bundleFeedingFromAnyInbox: RuleBundleModel[] = [];
  const bundleFeedingFromAnyTeamInbox: RuleBundleModel[] = [];
  const bundleFeedingFromAnyIndividualInbox: RuleBundleModel[] = [];
  const inboxInToBundlesMap = new Map<number, RuleBundleModel[]>();

  moveInBundles.forEach((ruleBundle) => {
    const bundleImpact = listImpactedInboxesByBundle(ruleBundle, listImpactedInboxes);
    if (!bundleImpact) {
      return;
    }
    // If bundle impact all team and individual inboxes, add it to the fromAnyInbox bag.
    if (bundleImpact.impactsAllTeamInboxes && bundleImpact.impactsAllIndividualInboxes) {
      bundleFeedingFromAnyInbox.push(ruleBundle);
      return;
    }
    // If bundle impact all team inboxes, add it to the fromAnyTeamInbox bag.
    if (bundleImpact.impactsAllTeamInboxes) {
      bundleFeedingFromAnyTeamInbox.push(ruleBundle);
      return;
    }
    // If bundle impact all individual inboxes, add it to the fromAnyIndividualInbox bag.
    if (bundleImpact.impactsAllIndividualInboxes) {
      bundleFeedingFromAnyIndividualInbox.push(ruleBundle);
      return;
    }
    // Else add it to all the inbox's bag it impacts.
    [...bundleImpact.individualInboxes, ...bundleImpact.teamInboxes].forEach((inboxIn) => {
      const bundles = inboxInToBundlesMap.get(inboxIn.id);
      if (bundles) {
        bundles.push(ruleBundle);
      } else {
        inboxInToBundlesMap.set(inboxIn.id, [ruleBundle]);
      }
    });
  });

  return {
    fromSpecificInbox: inboxInToBundlesMap,
    fromAnyInbox: bundleFeedingFromAnyInbox,
    fromAnyTeamInbox: bundleFeedingFromAnyTeamInbox,
    fromAnyIndividualInbox: bundleFeedingFromAnyIndividualInbox,
  };
}

/**
 * Compute all the inboxes to which conversations are move from this inbox.
 * Map them to the list of bundles performing this action.
 *
 * Return a map of:
 * - inboxId to which conversation are moved out of this inbox,
 * - to a collection of bundles that operate this move action.
 */
function getInboxesOut(
  moveOutBundles: ReadonlyArray<RuleBundleModelStrict>,
  getFedInbox: InboxGraphInheritableProps['getFedInbox'],
) {
  const inboxOutToBundlesMap = new Map<number, RuleBundleModel[]>();
  moveOutBundles.forEach((ruleBundle) => {
    const inboxOut = ruleBundle.rules
      .map((rule) => getFedInbox(rule.actions as RuleAction[]))
      .filter((_inbox) => _inbox !== undefined)
      .pop();

    if (!inboxOut) {
      return;
    }

    const bundles = inboxOutToBundlesMap.get(inboxOut.id);
    if (bundles) {
      bundles.push(ruleBundle);
    } else {
      inboxOutToBundlesMap.set(inboxOut.id, [ruleBundle]);
    }
  });

  return inboxOutToBundlesMap;
}
